import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { z } from "zod/v3";
import { kernelQuery, kernelMutation, getWorkspaceContext, requireWriteAccess, recordSessionActivity, getAgentSessionId } from "../client.js";
import { formatRubricCoaching } from "./smart-capture.js";
import { runConflictPreflight } from "./conflict-preflight.js";
import { trackQualityVerdict, trackQualityCheck, trackChainEntryCommitted, trackFieldQualityWarning, trackCommitErrorByCode } from "../analytics.js";
import { extractPreview, deriveEpistemicStatus, formatEpistemicLine, toEpistemicInput } from "./knowledge-helpers.js";
import { success, notFoundResult, successResult, type NextAction, type CaptureContract, type ToolResult } from "../envelope.js";
// WP-465 surface parity (TEN-2458): extracted to coherency-refusal.ts to break the circular dep with smart-capture.ts.
import { coherencyRefusedResult, type CoherencyRefusalPayload } from "./coherency-refusal.js";
export { coherencyRefusedResult, type CoherencyRefusalPayload } from "./coherency-refusal.js";

// WP-640 S2 (TASK-63): BET-68's legacy `status`→`workflowStatus` shim RETIRED EARLY, per
// ASM-43 (zero external consumers) — not date-locked to its originally-promised ~2026-09-03
// sunset. The WORKFLOW_STATUS_VALUES frozen literal, the WorkflowStatus type, and
// LEGACY_WORKFLOW_STATUSES routing that used to live here are gone; `status` now accepts ONLY
// the lifecycle enum, hard, with no auto-routing or deprecation warning. This is a distinct
// workstream from the WP-640 era-collapse rename (TEN-3079) — coincidentally the frozen literal
// used to carry a stray bet value in its "Chains (bet type) lifecycle" comment block, but that
// was BET-68's OWN historical artifact, not this slice's canonicalKey era shim.

type LifecycleStatus = 'draft' | 'active' | 'deprecated' | 'archived';

export const updateEntrySchema = z.object({
  entryId: z.string().describe("Entry ID to update, e.g. 'T-SUPPLIER', '<PREFIX>-<n>'"),
  name: z.string().optional().describe("New display name"),
  // Chain-ID-leak guard (check:chain-id-leaks): the retired legacy-shim ticket number stays in
  // the code comment above, never in this user-visible describe() string.
  status: z.enum(['draft', 'active', 'deprecated', 'archived']).optional().describe("Lifecycle status: draft | active | deprecated | archived. Use `workflowStatus` for collection-specific workflow values — those are no longer accepted via `status`."),
  workflowStatus: z.string().optional().describe("Collection workflow state. Valid values are collection-specific and server-owned — discover them via `collections action=describe` for the target collection. The server rejects invalid values and returns the valid set in the error."),
  data: z.record(z.unknown()).optional().describe("Fields to update (merged with existing data)"),
  order: z.number().optional().describe("New sort order"),
  canonicalKey: z.string().optional().describe("Semantic type (e.g. 'decision', 'tension'). Only changeable on draft/uncommitted entries."),
  autoPublish: z.boolean().optional().default(false).describe("Only true when user explicitly asks to publish. Default false = draft. Never auto-publish without user confirmation."),
  changeNote: z.string().optional().describe("Strongly recommended: short human-readable rationale for WHY this change was made (e.g. 'Aligned description with F1-themed copy'). Surfaces in activity feed and pb get. If omitted, falls back to session purpose or auto-generated field summary."),
  sourceRef: z.string().optional().describe("URI or path of the source document backing this entry. Write-once: can only be set if currently empty."),
  sourceExcerpt: z.string().optional().describe("Verbatim excerpt from the source that backs this entry's claims. Write-once: can only be set if currently empty."),
  // WP-465 slice ⑤ — relay-only (TEN-2233): validation/min-length/recording live in Convex.
  steeringOverrideReason: z.string().optional().describe("Typed override (≥12 chars) that clears a steering coherency block on a misaligned governance write — always recorded with author attribution."),
  coherencyAcknowledgement: z.object({
    response: z.enum(['linked', 'accepted-fix', 'diverged']).describe("Explicit response to an acknowledge-required coherency challenge."),
    entryId: z.string().optional().describe("Authorizing entry being linked/accepted (required for 'linked' and 'accepted-fix')."),
    reason: z.string().optional().describe("Divergence rationale (required for 'diverged', ≥12 chars)."),
  }).optional().describe("Explicit response to a coherency challenge (standard/strict workspace modes). One acknowledgement per challenge per entry per session."),
});


// getHistorySchema/handleGetHistory moved to ./knowledge/getHistory.ts (STD-2/DEC-1504
// shrink-only offset, WP-582 B1) — re-exported here so existing importers of "./knowledge.js"
// are unaffected.
export { getHistorySchema, handleGetHistory } from "./knowledge/getHistory.js";

export const commitEntrySchema = z.object({
  entryId: z.string().describe("Entry ID to accept, e.g. 'TEN-abc123', '<PREFIX>-<n>'"),
  // WP-316 S3: Preview gate — dry-run mode. Returns would-succeed result, no DB writes.
  preview: z.boolean().optional().describe("If true, validates the accept without writing. Returns what would happen. Default false."),
  // WP-465 slice ⑤ — relay-only (TEN-2233): validation/recording live in Convex.
  steeringOverrideReason: z.string().optional().describe("Typed override (≥12 chars) that clears a steering coherency block at the publish chokepoint — always recorded with author attribution."),
  coherencyAcknowledgement: z.object({
    response: z.enum(['linked', 'accepted-fix', 'diverged']).describe("Explicit response to an acknowledge-required coherency challenge."),
    entryId: z.string().optional().describe("Authorizing entry being linked/accepted (required for 'linked' and 'accepted-fix')."),
    reason: z.string().optional().describe("Divergence rationale (required for 'diverged', ≥12 chars)."),
  }).optional().describe("Explicit response to a coherency challenge (standard/strict workspace modes)."),
});

/**
 * WP-484 S1 (§3/§4 entries-family migration): update-entry's handler body,
 * extracted so BOTH the legacy `update-entry` tool AND the new
 * `entries action="update"` variant call the SAME implementation — one
 * business-logic path. WP-484 S3 (clean break, TEN-2157): the standalone
 * `update-entry` tool retired — `entries` (action=update) is the only
 * remaining caller, so `toolName` is always "entries" now. The parameter
 * stays (rather than hardcoding the literal inline) so a future caller with
 * a different self-reference doesn't have to plumb a new argument through.
 */
export async function handleUpdateEntry(
  { entryId, name, status: rawStatus, workflowStatus: rawWorkflowStatus, data, order, canonicalKey, autoPublish, changeNote, sourceRef, sourceExcerpt, steeringOverrideReason, coherencyAcknowledgement }: z.infer<typeof updateEntrySchema>,
  toolName: "entries" = "entries",
): Promise<ToolResult> {
      requireWriteAccess();

      const fieldsProvided: string[] = [];
      if (name !== undefined) fieldsProvided.push('name');
      if (rawStatus !== undefined) fieldsProvided.push('status');
      if (rawWorkflowStatus !== undefined) fieldsProvided.push('workflowStatus');
      if (data !== undefined) fieldsProvided.push('data');
      if (order !== undefined) fieldsProvided.push('order');
      if (canonicalKey !== undefined) fieldsProvided.push('canonicalKey');

      // WP-640 S2 (TASK-63): BET-68's legacy status→workflowStatus auto-routing (and its
      // deprecation-warning response line, below) retired — `status` is validated hard by the
      // schema above to the lifecycle enum, so no runtime routing/coercion is needed here.
      const status: LifecycleStatus | undefined = rawStatus;
      const workflowStatus: string | undefined = rawWorkflowStatus;

      // BET-192 / FEAT-575: chain.updateEntry now returns warnings + normalization breakdown
      const updateResult = await kernelMutation<GatewayRouteReturnByName<"chain.updateEntry">>("chain.updateEntry", {
        entryId,
        name,
        status,
        workflowStatus,
        data,
        order,
        canonicalKey,
        autoPublish,
        changeNote,
        changedBy: getAgentSessionId() ? `agent:${getAgentSessionId()}` : undefined,
        ...(sourceRef ? { sourceRef } : {}),
        ...(sourceExcerpt ? { sourceExcerpt } : {}),
        ...(steeringOverrideReason ? { steeringOverrideReason } : {}),
        ...(coherencyAcknowledgement ? { coherencyAcknowledgement } : {}),
      });

      // WP-465 slice ⑤ (Option B): a refused write RETURNS the structured payload —
      // render it as a failure with the routes; never as a silent success.
      const maybeRefused = updateResult as unknown as { outcome?: string; refusal?: CoherencyRefusalPayload };
      if (maybeRefused.outcome === 'refused' && maybeRefused.refusal) {
        return coherencyRefusedResult(`${toolName} action=update`, entryId, maybeRefused.refusal);
      }

      const id = updateResult.id;
      await recordSessionActivity({ entryModified: id });

      const wsCtx = await getWorkspaceContext();
      const updated = await kernelQuery<any>("chain.getEntry", { entryId });
      const entryStatus: LifecycleStatus = (updated?.status ?? 'draft') as LifecycleStatus;
      const versionMode = entryStatus === 'active' ? 'published' : `saved as ${entryStatus}`;
      const responseLines = [
        `# Entry Updated`,
        ``,
        `**${entryId}** — ${versionMode}.`,
        ``,
        `Internal ID: ${id}`,
        `**Workspace:** ${wsCtx.workspaceSlug} (${wsCtx.workspaceId})`,
      ];

      if (fieldsProvided.length > 0) {
        responseLines.push(`**Fields provided:** ${fieldsProvided.join(', ')}`);
      } else {
        responseLines.push('');
        responseLines.push('⚠️ No fields to update were provided — only `entryId` was set.');
      }

      // BET-192 / FEAT-575: Surface normalization warnings (fields silently dropped by normalizeEntryData)
      if ((updateResult.normalizationWarnings ?? []).length > 0) {
        responseLines.push('');
        responseLines.push('⚠️ **Normalization warnings:**');
        for (const warning of updateResult.normalizationWarnings ?? []) {
          responseLines.push(`  - ${warning}`);
        }
      }

      // BET-192 / FEAT-575: Surface validation warnings (missing required fields, etc.)
      if ((updateResult.validationWarnings ?? []).length > 0) {
        responseLines.push('');
        responseLines.push('⚠️ **Validation warnings:**');
        for (const warning of updateResult.validationWarnings ?? []) {
          responseLines.push(`  - ${warning}`);
        }
      }

      const summary = `Updated ${entryId} — ${versionMode}. Fields: ${fieldsProvided.join(', ') || 'none'}.`;
      const next: NextAction[] = [
        { tool: toolName, description: "View updated entry", parameters: { action: "get", entryId } },
        { tool: toolName, description: "Accept onto Chain", parameters: { action: "commit", entryId } },
      ];

      return {
        content: [{ type: "text" as const, text: responseLines.join("\n") }],
        structuredContent: success(summary, {
          entryId: id,
          versionMode,
          fieldsProvided,
          // BET-192: normalization breakdown in structuredContent — symmetric with capture tool
          ...(updateResult.normalization && (Object.keys(updateResult.normalization.remapped).length > 0 || updateResult.normalization.rejected.length > 0) && {
            normalization: {
              remapped: updateResult.normalization.remapped,
              rejected: updateResult.normalization.rejected,
            },
          }),
          ...((updateResult.normalizationWarnings ?? []).length > 0 && {
            normalizationWarnings: updateResult.normalizationWarnings,
          }),
          ...((updateResult.validationWarnings ?? []).length > 0 && {
            validationWarnings: updateResult.validationWarnings,
          }),
        }, next),
      };
}

/**
 * WP-484 S1/S3: commit-entry's handler body. WP-484 S3 (clean break,
 * TEN-2157): the standalone `commit-entry` tool retired — `entries`
 * (action=commit) is the only remaining caller, same `toolName` convention
 * as `handleUpdateEntry`.
 */
export async function handleCommitEntry(
  { entryId, preview, steeringOverrideReason, coherencyAcknowledgement }: z.infer<typeof commitEntrySchema>,
  toolName: "entries" = "entries",
): Promise<ToolResult> {
      requireWriteAccess();

      const entry = await kernelQuery<any>("chain.getEntry", { entryId });
      if (!entry) {
        return notFoundResult(entryId, `Entry '${entryId}' not found. Try search to find the right ID.`);
      }

      const descField = entry.data?.description ?? entry.data?.canonical ?? entry.data?.rationale ?? "";
      const preflight = await runConflictPreflight(
        entry.name,
        descField,
        entry.collectionSlug ?? entry.collection?.slug ?? entry.collection?.name,
      );
      // WP-485 Slice 2b (R2, FEAT-1370): every discovered conflict is now shown as visible
      // advisory text — no local "blocking" filter. The old branch below this comment used to
      // hard-block on `preflight.blockingContradictions.length > 0` via a client-side
      // BLOCKING_CONTRADICTION_THRESHOLD comparison (deleted from conflict-preflight.ts); that
      // enforcement decision now happens ONE place, server-side, inside commitEntry's gate
      // (Slice 2a) — this tool no longer decides, it only discovers and displays.
      const advisoryWarnings = preflight.source === "semantic"
        ? preflight.conflicts.map((conflict) => ({
            kind: "semantic" as const,
            entryId: conflict.chainEntryId,
            name: conflict.chainEntryName,
            explanation: conflict.explanation,
          }))
        : preflight.advisoryWarnings.map((warning) => ({
            kind: "heuristic" as const,
            entryId: warning.entryId,
            name: warning.name,
            collection: warning.collection,
            governsCount: warning.governsCount,
          }));

      if (advisoryWarnings.length > 0) {
        await recordSessionActivity({ contradictionWarning: true });
      }

      // WP-485 Slice 2b round 2 (Codex P1, FEAT-1370): forward the discovered semantic
      // conflicts to commitEntry as evidence — `resolveContradictionVerdict` reads ONLY
      // caller-fed conflicts (mutations can't call the LLM-backed detector themselves), so
      // without this the server gate always evaluates an empty set on this path, no matter
      // how genuine the discovered contradiction is. Heuristic-source `advisoryWarnings` are a
      // different shape (`ContradictionWarning`, not `SemanticConflictArg`) and are never
      // forwarded. Whitelist only the validator-accepted fields — `chain.detectSemanticConflicts`
      // also returns an internal `chainEntryDocId` that fails commitEntry's `conflicts` arg
      // validator (live-proof finding, Slice 2b round 1).
      const conflictEvidence = preflight.source === "semantic"
        ? preflight.conflicts.map((c) => ({
            chainEntryId: c.chainEntryId,
            chainEntryName: c.chainEntryName,
            ...(c.chainEntryDescription ? { chainEntryDescription: c.chainEntryDescription } : {}),
            type: c.type,
            confidence: c.confidence,
            explanation: c.explanation,
          }))
        : [];

      let coachingResult: any = null;
      // A REAL commit re-evaluates here so the gate (inside chain.commitEntry) reads a
      // fresh verdict for the current content — otherwise a stale passing row lets the
      // commit through on content that changed.
      //
      // Codex P1 (PR #291): a PREVIEW must NOT run this — it used to insert a throwaway
      // verdict row (so the preview probe would read a fresh judgment), but that row
      // polluted usage stats and became the entry's latest verdict, leaving it
      // un-promotable after a mere dry run. The preview now gets parity a different way:
      // chain.commitEntry's dry-run path computes the same verdict in memory and classifies
      // it (buildCommitPreviewVerdictRow → simulatedVerdictRow), writing nothing. So skip
      // the re-evaluate entirely on preview.
      if (!preview) {
        try {
          coachingResult = await kernelMutation<GatewayRouteReturnByName<"quality.evaluateHeuristicAndSchedule">>("quality.evaluateHeuristicAndSchedule", {
            entryId,
            context: "commit",
          });
        } catch {
          // Quality coaching is advisory
        }
      }

      let result: any;
      try {
        result = await kernelMutation<GatewayRouteReturnByName<"chain.commitEntry">>("chain.commitEntry", {
          entryId,
          author: getAgentSessionId() ? `agent:${getAgentSessionId()}` : undefined,
          sessionId: getAgentSessionId() ?? undefined,
          ...(preview ? { preview: true } : {}),
          ...(steeringOverrideReason ? { steeringOverrideReason } : {}),
          ...(coherencyAcknowledgement ? { coherencyAcknowledgement } : {}),
          ...(conflictEvidence.length > 0 ? { conflicts: conflictEvidence } : {}),
        });
      } catch (commitErr: unknown) {
        // WP-316 S1a: Fire PostHog event on structured validation failure before re-throwing.
        // The thinWrapper HOF will catch and format the error into diagnostics.
        const errCode = (commitErr as { code?: string })?.code;
        if (errCode === "VALIDATION_FAILED") {
          try {
            const wsCtx = await getWorkspaceContext();
            const e = commitErr as { missingRequiredFields?: string[]; fieldErrors?: string[] };
            trackCommitErrorByCode(wsCtx.workspaceId, {
              error_code: errCode,
              missing_field_count: e.missingRequiredFields?.length ?? 0,
              field_error_count: e.fieldErrors?.length ?? 0,
              entry_id: entryId,
            });
          } catch { /* tracking must not break commit error path */ }
        }
        throw commitErr;
      }

      // WP-465 slice ⑤ (Option B): a refused accept RETURNS the structured payload —
      // render it as a failure with the routes; never as a silent success.
      if (result?.status === 'coherency_refused' && result?.refusal) {
        return coherencyRefusedResult(`${toolName} action=commit`, entryId, result.refusal as CoherencyRefusalPayload);
      }

      // WP-316 S3: Preview gate — return dry-run result without recording activity.
      if (result?.preview) {
        const wouldBlock = result.wouldBlock === true;
        // WP-485 Slice 2b round 3 (Codex P1, FEAT-1370): commitEntry's preview path attaches
        // `contradictionAdvisory` for parity with a real commit (Slice 2a) — this early return
        // sits BEFORE the contradictionAdvisory rendering further down, so preview silently
        // dropped it. Mirror it here so `preview:true` and a real commit report the same
        // advisory for identical `conflicts` evidence.
        const previewContradictionAdvisory = result?.contradictionAdvisory as
          | { reason: string; blockingCount: number; conflictDetails: Array<{ chainEntryId: string; chainEntryName: string; explanation: string }> }
          | undefined;
        const previewEnvelope = success(
          `Preview: ${wouldBlock ? "would BLOCK accept" : "would accept"} ${entryId} — no DB writes`,
          {
            entryId,
            name: entry.name,
            outcome: wouldBlock ? ("blocked" as const) : ("preview" as const),
            currentStatus: result.currentStatus,
            wouldSetStatus: result.wouldSetStatus,
            wouldBlock,
            ...(wouldBlock && result.blockingReason ? { blockingReason: result.blockingReason } : {}),
            ...(previewContradictionAdvisory ? { contradictionAdvisory: previewContradictionAdvisory } : {}),
          },
          [{ tool: toolName, description: "Accept for real", parameters: { action: "commit", entryId } }],
        );
        if (result.contract) {
          previewEnvelope.contract = result.contract;
        }
        const blockingNote = wouldBlock && result.blockingReason
          ? `\n\n**Would be blocked:** ${result.blockingReason}`
          : wouldBlock
            ? `\n\n**Would be blocked** by the coherency/grounding gate.`
            : "";
        const advisoryNote = previewContradictionAdvisory
          ? `\n\n⚠ Contradiction advisory (would be attached on real commit): ${previewContradictionAdvisory.reason}\n${previewContradictionAdvisory.conflictDetails.map((c) => `- \`${c.chainEntryId}\` ${c.chainEntryName}: ${c.explanation}`).join("\n")}`
          : "";
        return {
          content: [{ type: "text" as const, text: `# Preview: would ${wouldBlock ? "BLOCK accept" : "accept"} ${entryId}\n\nCurrent status: \`${result.currentStatus}\` → would become \`${result.wouldSetStatus}\`.${blockingNote}${advisoryNote}\n\nNo DB writes — call without \`preview:true\` to accept for real.` }],
          structuredContent: previewEnvelope,
        };
      }

      const docId = result?._id ?? entry._id;
      const wsCtx = await getWorkspaceContext();
      const isProposal = result?.status === "proposal_created";
      if (!isProposal) {
        await recordSessionActivity({ entryModified: docId });
        const coll =
          (entry.collectionSlug as string | undefined) ??
          (entry.collection?.slug as string | undefined);
        trackChainEntryCommitted(wsCtx.workspaceId, {
          entry_id: entry.entryId ?? entryId,
          collection: coll,
          commit_method: "manual",
          surface: "mcp_commit_tool",
        });
      }
      let lines: string[];

      if (isProposal) {
        lines = [
          `# Proposal created: ${result.entryId ?? entryId}`,
          `**${result.name ?? entry.name}** — accepting requires consent. A proposal was created instead of accepting directly.`,
          `**Workspace:** ${wsCtx.workspaceSlug} (${wsCtx.workspaceId})`,
          "",
          result.existing
            ? "An open proposal for this entry already exists."
            : "Affected owners will be notified. The entry will be accepted when the consent window expires with no objections, or when all owners approve.",
        ];
      } else {
        lines = [
          `# Accepted: ${entryId}`,
          `**${entry.name}** promoted to SSOT on the Chain.`,
          `**Workspace:** ${wsCtx.workspaceSlug} (${wsCtx.workspaceId})`,
        ];
        // WP-B (attestation spec §4.3): THE RECEIPT SAYS SO — the server attested this
        // entry as part of the promote (promote-gate context). Rendered, never re-derived.
        const autoVerified = result?.autoVerified as { strength?: string; verifiedBy?: string } | undefined;
        if (autoVerified?.strength) {
          const who = autoVerified.verifiedBy ? ` (${autoVerified.verifiedBy})` : "";
          lines.push("");
          lines.push(`_Auto-verified as part of promote — ${autoVerified.strength}${who}._`);
        }
      }

      if (advisoryWarnings.length > 0) {
        lines.push("");
        lines.push(preflight.source === "semantic"
          ? "⚠ Semantic conflict preflight: advisory conflict(s) found."
          : "⚠ Contradiction check: proposed entry matched existing governance entries:");
        for (const w of advisoryWarnings) {
          lines.push(w.kind === "semantic"
            ? `- ${w.name} (${w.entryId}) — ${w.explanation}`
            : `- ${w.name} (${w.collection}, ${w.entryId}) — has 'governs' relation to ${w.governsCount} entries`);
        }
        // WP-485 Slice 2b round 4 (Codex P2, FEAT-1370): this block renders AFTER the entry
        // has already been accepted (R2 deleted the client-side block that used to make
        // "before accepting" true) — the recovery instruction must be a post-commit action,
        // not an impossible-to-follow "before" instruction for an accept that already happened.
        lines.push(preflight.source === "semantic"
          ? "Run context action=gather on the conflicting entries to review; use entries action=update to reconcile the two, or to mark this entry status='deprecated' if it's actually superseded."
          : "Run context action=gather on these entries to review; use entries action=update to reconcile the two, or to mark this entry status='deprecated' if it's actually superseded.");
      }

      if (coachingResult?.verdict) {
        try {
          const v = coachingResult.verdict;
          const failedCount = (v.criteria ?? []).filter((c: any) => !c.passed).length;
          trackQualityVerdict(wsCtx.workspaceId, {
            entry_id: entryId,
            entry_type: v.canonicalKey ?? entry.canonicalKey ?? "",
            tier: v.tier,
            context: "commit",
            passed: v.passed,
            source: coachingResult.source ?? "heuristic",
            criteria_total: v.criteria?.length ?? 0,
            criteria_failed: failedCount,
            llm_scheduled: v.tier !== "passive",
            methodology_version: v.methodologyVersion,
          });
        } catch { /* tracking is advisory */ }
      }

      if (coachingResult?.verdict && coachingResult.verdict.tier !== "passive" && coachingResult.verdict.criteria.length > 0) {
        const coaching = formatRubricCoaching(coachingResult);
        if (coaching) {
          lines.push("");
          lines.push(coaching);
          if (!coachingResult.verdict.passed && coachingResult.verdict.tier === "assertive") {
            lines.push("");
            lines.push("_This entry was accepted despite quality concerns. The override has been recorded._");
          }
        }
      }

      // BET-289 S5: Surface advisory guidance warnings from commit result + track
      const guidanceWarnings = result?.guidanceWarnings as Array<{ field: string; message: string; warningType: string }> | undefined;
      if (guidanceWarnings && guidanceWarnings.length > 0) {
        lines.push("");
        lines.push("## Field Guidance Warnings (advisory)");
        for (const w of guidanceWarnings) {
          lines.push(`- **${w.field}:** ${w.message}`);
        }
        lines.push(`_These are advisory — the entry was accepted. Use \`entries action=update entryId="${entryId}"\` to address them._`);

        const uniqueTypes = [...new Set(guidanceWarnings.map((w) => w.warningType))];
        trackFieldQualityWarning(wsCtx.workspaceId, {
          warning_count: guidanceWarnings.length,
          warning_types: uniqueTypes,
        });
      }

      // WP-465 slice ⑤ (Light-mode row-11): a non-blocking coherency advisory raised
      // when a misaligned governance draft is accepted. The accept proceeds; the
      // misalignment is surfaced alongside the other commit warnings, never enforced.
      const steeringAdvisory =
        typeof result?.steeringAdvisory === "string" && result.steeringAdvisory.trim()
          ? (result.steeringAdvisory as string)
          : undefined;
      if (steeringAdvisory) {
        lines.push("");
        lines.push(`⚠ Coherency advisory (entry accepted): ${steeringAdvisory}`);
      }

      // WP-485 Slice 2b round 2 (Codex P1, FEAT-1370): the server's own contradiction
      // verdict (DEC-1321 — advisory only, never blocking), distinct from the broader
      // discovery list rendered above — this is the gate's authoritative high-confidence
      // read of the conflicts forwarded as evidence, not every candidate found.
      const contradictionAdvisory = result?.contradictionAdvisory as
        | { reason: string; blockingCount: number; conflictDetails: Array<{ chainEntryId: string; chainEntryName: string; explanation: string }> }
        | undefined;
      if (contradictionAdvisory) {
        lines.push("");
        lines.push(`⚠ Contradiction advisory (entry committed): ${contradictionAdvisory.reason}`);
        for (const c of contradictionAdvisory.conflictDetails) {
          lines.push(`- \`${c.chainEntryId}\` ${c.chainEntryName}: ${c.explanation}`);
        }
      }

      // WP-582 B1 (FEAT-1403): direction-trace promote-time advisory (publishSeam accumulator).
      const publishAdvisories = Array.isArray(result?.publishAdvisories)
        ? (result.publishAdvisories as unknown[]).filter((a): a is string => typeof a === "string")
        : undefined;
      if (publishAdvisories && publishAdvisories.length > 0) {
        lines.push("");
        lines.push("⚠ Direction advisory (entry committed):");
        for (const advisory of publishAdvisories) {
          lines.push(`- ${advisory}`);
        }
      }

      // Epistemic advisory for insights without evidence (BR-85)
      const epistemic = deriveEpistemicStatus(toEpistemicInput(entry as Record<string, unknown>));

      if (epistemic && (epistemic.level === "hypothesis" || epistemic.level === "untested")) {
        lines.push("");
        lines.push(`⚠ ${formatEpistemicLine(epistemic)}`);
        if (epistemic.action) {
          lines.push(`  → ${epistemic.action}`);
        }
      }

      // WP-316 S1a: Fetch capture contract so agents know required fields after commit.
      // Advisory — failure must not block the commit response.
      let captureContract: CaptureContract | undefined;
      try {
        const collSlug =
          (entry.collectionSlug as string | undefined) ??
          (entry.collection?.slug as string | undefined);
        if (collSlug) {
          captureContract = await kernelQuery<GatewayRouteReturnByName<"chain.getCaptureContract">>(
            "chain.getCaptureContract",
            { collectionSlug: collSlug },
          ) ?? undefined;
        }
      } catch { /* contract fetch is advisory */ }

      // Context-sensitive next: proposal vs. direct commit have different follow-ups
      const next: NextAction[] = isProposal
        ? [
            { tool: "entries", description: "View entry", parameters: { action: "get", entryId } },
          ]
        : [
            { tool: "relations", description: "Discover connections", parameters: { action: "suggest", entryId } },
            { tool: "entries", description: "View accepted entry", parameters: { action: "get", entryId } },
          ];

      const summary = isProposal
        ? `Proposal created for ${entryId} (${entry.name}). Awaiting consent.`
        : `Accepted ${entryId} (${entry.name}) onto the Chain.`;

      const commitSuccessEnvelope = success(
        summary,
        {
          entryId,
          name: entry.name,
          outcome: isProposal ? "proposal_created" as const : "committed" as const,
          qualityVerdict: coachingResult?.verdict ?? undefined,
          source: coachingResult?.source ?? undefined,
          contradictions: advisoryWarnings.length,
          ...(epistemic ? { epistemicStatus: epistemic } : {}),
          ...(steeringAdvisory ? { steeringAdvisory } : {}),
          // WP-485 Slice 2b round 2 (Codex P1, FEAT-1370): mirror the server's contradiction
          // advisory into the structured payload so --json/agent consumers see it too.
          ...(contradictionAdvisory ? { contradictionAdvisory } : {}),
          // WP-582 B1 (FEAT-1403): mirror the direction-trace advisory into the structured payload.
          ...(publishAdvisories && publishAdvisories.length > 0 ? { publishAdvisories } : {}),
          // WP-B (attestation spec §4.3): mirror the auto-verify receipt into the
          // structured payload so --json/agent consumers see it too.
          ...(result?.autoVerified ? { autoVerified: result.autoVerified } : {}),
        },
        next,
      );
      // WP-316 S1a: Attach capture contract so agents know required fields for next capture.
      if (captureContract) {
        commitSuccessEnvelope.contract = captureContract;
      }

      return {
        content: [{ type: "text" as const, text: lines.join("\n") }],
        structuredContent: commitSuccessEnvelope,
      };
}

// WP-484 S3 (clean break, TEN-2157): the standalone `update-entry` /
// `get-history` / `commit-entry` tool registrations retired here.
// handleUpdateEntry / handleGetHistory / handleCommitEntry stay live —
// entries.ts (the `entries` compound) is their only remaining caller.

